import React, { useEffect, useState, useRef } from "react"
import {
  XMarkIcon,
  ChevronDownIcon,
  CheckIcon,
} from "@heroicons/react/24/outline"
import moment from "moment"
import VariantReader from "aldoo-ra/VariantReader"
import { twMerge } from "tailwind-merge"

// MonthSelector Component
const MonthSelector = ({ viewDate, onSelect, onClose, variants }) => {
  const [showMonths, setShowMonths] = useState(true)
  const currentYear = moment().year()
  const yearsRange = Array.from({ length: 15 }, (_, i) => currentYear - 7 + i)

  return (
    <div className={variants?.monthSelector?.container || ""}>
      {/* Header */}
      <div
        className={
          variants?.monthSelector?.header?.wrapper ||
          "flex items-center justify-between mb-6"
        }
      >
        <span
          className={
            variants?.monthSelector?.header?.format || "text-admin_text/60"
          }
        >
          MM/DD/YYYY
        </span>
        <button onClick={onClose}>
          <XMarkIcon
            className={
              variants?.monthSelector?.header?.closeButton ||
              "w-6 h-6 text-admin_text/60 cursor-pointer"
            }
          />
        </button>
      </div>

      {/* Month/Year Selection */}
      <div
        className={
          variants?.monthSelector?.selectors?.wrapper || "flex gap-4 mb-6"
        }
      >
        {/* Month Dropdown */}
        <div className="relative flex-1">
          <button
            onClick={() => setShowMonths(true)}
            className={twMerge(
              variants?.monthSelector?.selectors?.button?.base ||
                "w-full text-left px-4 py-2 rounded-lg",
              showMonths
                ? variants?.monthSelector?.selectors?.button?.active ||
                    "bg-text/10"
                : variants?.monthSelector?.selectors?.button?.hover ||
                    "hover:bg-text/5"
            )}
            style={{ height: "48px" }}
          >
            <span>{viewDate.format("MMM")}</span>
            <ChevronDownIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Year Selection */}
        <div className="flex-1">
          <button
            onClick={() => setShowMonths(false)}
            className={twMerge(
              variants?.monthSelector?.selectors?.button?.base ||
                "w-full text-left px-4 py-2 rounded-lg",
              !showMonths
                ? variants?.monthSelector?.selectors?.button?.active ||
                    "bg-text/10"
                : variants?.monthSelector?.selectors?.button?.hover ||
                    "hover:bg-text/5"
            )}
            style={{ height: "48px" }}
          >
            <span>{viewDate.format("YYYY")}</span>
            <ChevronDownIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Scrollable Container */}
      <div className="overflow-y-auto max-h-[calc(480px)] scrollbar-thin scrollbar-thumb-text/10 scrollbar-track-transparent">
        {showMonths ? (
          /* Months List */
          <div
            className={
              variants?.monthSelector?.monthList?.wrapper || "space-y-2"
            }
          >
            {moment.months().map((month, index) => {
              const isSelected = index === viewDate.month()
              return (
                <button
                  key={month}
                  onClick={() => onSelect({ type: "month", value: index })}
                  className={twMerge(
                    variants?.monthSelector?.monthList?.item?.base ||
                      "w-full px-4 py-3 rounded-lg text-left flex items-center",
                    isSelected
                      ? variants?.monthSelector?.monthList?.item?.selected ||
                          "bg-primary/10"
                      : variants?.monthSelector?.monthList?.item?.hover ||
                          "hover:bg-text/5"
                  )}
                >
                  {month}
                </button>
              )
            })}
          </div>
        ) : (
          /* Years List */
          <div
            className={
              variants?.monthSelector?.monthList?.wrapper || "space-y-2"
            }
          >
            {yearsRange.map((year) => {
              const isSelected = year === viewDate.year()
              return (
                <button
                  key={year}
                  onClick={() => onSelect({ type: "year", value: year })}
                  className={twMerge(
                    variants?.monthSelector?.monthList?.item?.base ||
                      "w-full px-4 py-3 rounded-lg text-left flex items-center",
                    isSelected
                      ? variants?.monthSelector?.monthList?.item?.selected ||
                          "bg-primary/10"
                      : variants?.monthSelector?.monthList?.item?.hover ||
                          "hover:bg-text/5"
                  )}
                >
                  {year}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const DatePicker = ({
  value,
  onChange,
  range = false,
  variant = "default",
  styleOverrides = {},
  dateFormat = "DD.MM.YY",
  monthFormat = "MMMM YYYY",
  showToday = true,
}) => {
  // Use variant system
  const variants = VariantReader.useVariant({
    componentName: "DatePicker",
    variant,
    styleOverrides,
  })

  // Use ref to track previous value for comparison
  const prevValueRef = useRef(null)

  // Initialize dates with state initializer function
  const [dates, setDates] = useState(() => {
    const initialStartDate = value?.startDate
      ? moment(value.startDate)
      : value
      ? moment(value)
      : null
    const initialEndDate = value?.endDate
      ? moment(value.endDate)
      : range
      ? null
      : initialStartDate

    return {
      start: initialStartDate && initialStartDate.isValid() ? initialStartDate : null,
      end: initialEndDate && initialEndDate.isValid() ? initialEndDate : null,
    }
  })

  const [isOpen, setIsOpen] = useState(false)
  const [showMonthSelector, setShowMonthSelector] = useState(false)
  const [hoverDate, setHoverDate] = useState(null)
  const [selectionStep, setSelectionStep] = useState("start")
  const [viewDate, setViewDate] = useState(() => {
    const startDate = value?.startDate
      ? moment(value.startDate)
      : value
      ? moment(value)
      : moment()
    return startDate.isValid() ? startDate : moment()
  })
  const [forceRender, setForceRender] = useState(0)

  // Compare values for equality
  const areValuesEqual = (val1, val2) => {
    if (val1 === val2) return true
    if (!val1 && !val2) return true
    if (!val1 || !val2) return false

    // Handle object format {startDate, endDate}
    if (val1.startDate && val2.startDate) {
      const startEqual = moment(val1.startDate).isSame(
        moment(val2.startDate),
        "day"
      )
      const endEqual =
        val1.endDate && val2.endDate
          ? moment(val1.endDate).isSame(moment(val2.endDate), "day")
          : val1.endDate === val2.endDate
      return startEqual && endEqual
    }

    // Handle direct date values - ensure valid moments before comparison
    const moment1 = moment(val1)
    const moment2 = moment(val2)
    
    if (!moment1.isValid() || !moment2.isValid()) return false
    
    return moment1.isSame(moment2, "day")
  }

  // Handle external value changes
  useEffect(() => {
    // Skip if value hasn't changed
    if (areValuesEqual(value, prevValueRef.current)) {
      return
    }

    const newStartDate = value?.startDate
      ? moment(value.startDate)
      : value
      ? moment(value)
      : null

    const newEndDate = value?.endDate
      ? moment(value.endDate)
      : range
      ? null
      : newStartDate

    // Only check for changes if we have valid dates
    const startChanged = newStartDate && newStartDate.isValid() && (!dates.start || !newStartDate.isSame(dates.start, "day"))
    const endChanged = range && newEndDate && newEndDate.isValid() &&
      (!dates.end || !newEndDate.isSame(dates.end, "day"))

    if (startChanged || endChanged || !prevValueRef.current) {
      setDates({
        start: newStartDate && newStartDate.isValid() ? newStartDate : dates.start,
        end: newEndDate && newEndDate.isValid() ? newEndDate : dates.end,
      })
      if (newStartDate && newStartDate.isValid()) {
        setViewDate(newStartDate)
      }
    }

    prevValueRef.current = value
  }, [value, range])

  const handleDateSelect = (date) => {
    if (!range) {
      setDates({ start: date, end: null })
      setForceRender(prev => prev + 1) // Force re-render
      // Don't call onChange here - wait for OK button
      return
    }

    if (selectionStep === "start" || !dates.start) {
      setDates((prev) => ({ ...prev, start: date, end: null }))
      setSelectionStep("end")
      setForceRender(prev => prev + 1) // Force re-render
    } else {
      const newDates = date.isBefore(dates.start)
        ? { start: date, end: dates.start }
        : { start: dates.start, end: date }

      setDates(newDates)
      setSelectionStep("start")
      setForceRender(prev => prev + 1) // Force re-render
      // Don't call onChange here either - wait for OK button
    }
  }

  const handleTodayClick = () => {
    const today = moment()
    if (!range) {
      setDates({ start: today, end: null })
      onChange?.(today.toDate())
    } else {
      setDates({ start: today, end: null })
      setSelectionStep("end")
    }
    setViewDate(today)
  }

  const isInRange = (date) => {
    if (!range || !dates.start) return false
    const end = dates.end || hoverDate
    if (!end) return false

    if (hoverDate) {
      return hoverDate.isBefore(dates.start)
        ? date.isBetween(hoverDate, dates.start, "day", "[]")
        : date.isBetween(dates.start, hoverDate, "day", "[]")
    }

    const isEndBeforeStart = dates.end?.isBefore(dates.start, "day")
    const [effectiveStart, effectiveEnd] = isEndBeforeStart
      ? [dates.end, dates.start]
      : [dates.start, dates.end]

    return date.isBetween(effectiveStart, effectiveEnd, "day", "[]")
  }

  const isRangeStart = (date) => dates.start && date.isSame(dates.start, "day")
  const isRangeEnd = (date) => dates.end && date.isSame(dates.end, "day")

  const handleDateSelection = ({ type, value: selectedValue }) => {
    const newDate = viewDate.clone()
    if (type === "month") {
      newDate.month(selectedValue)
    } else if (type === "year") {
      newDate.year(selectedValue)
    }
    setViewDate(newDate)
    setShowMonthSelector(false)
  }

  const generateDays = () => {
    const start = viewDate.clone().startOf("month").startOf("week")
    const end = viewDate.clone().endOf("month").endOf("week")
    const days = []
    let day = start.clone()

    while (day.isSameOrBefore(end)) {
      days.push(day.clone())
      day.add(1, "day")
    }
    return days
  }

  const getRangeStyle = (date) => {
    if (!range || !dates.start) return ""
    const end = dates.end || hoverDate
    if (!end) return ""

    const isStart = isRangeStart(date)
    const isEnd =
      isRangeEnd(date) || (hoverDate && date.isSame(hoverDate, "day"))
    const inRange = isInRange(date)
    const isSameStartEnd =
      dates.start && dates.end && dates.start.isSame(dates.end, "day")
    const isEndBeforeStart = hoverDate
      ? hoverDate.isBefore(dates.start, "day")
      : dates.end && dates.end.isBefore(dates.start, "day")

    let classes = ["rounded-none", "bg-primary", "text-white"]

    if (isSameStartEnd && isStart)
      return classes.concat("rounded-full").join(" ")
    if (isStart && !isEndBeforeStart)
      return classes.concat("rounded-l-full").join(" ")
    if (isEnd && !isEndBeforeStart)
      return classes.concat("rounded-r-full").join(" ")
    if (isEndBeforeStart && isStart)
      return classes.concat("rounded-r-full").join(" ")
    if (isEndBeforeStart && isEnd)
      return classes.concat("rounded-l-full").join(" ")
    if (inRange) return classes.join(" ")

    return ""
  }

  const formatDisplayDate = () => {
    if (!range) {
      return (dates.start && dates.start.isValid())
        ? dates.start.format(dateFormat)
        : "Select date"
    }
    if (!dates.start || !dates.start.isValid()) return "Select dates"
    if (!dates.end || !dates.end.isValid()) {
      return `${dates.start.format(dateFormat)} - Select end date`
    }
    return `${dates.start.format(dateFormat)} - ${dates.end.format(dateFormat)}`
  }

  return (
    <div className={variants?.container?.wrapper || "relative"}>
      <div
        onClick={() => setIsOpen(true)}
        className={
          variants?.input?.wrapper ||
          "cursor-pointer p-4 bg-admin_paper rounded-lg border border-admin_bordered"
        }
      >
        <div
          className={
            variants?.input?.label || "text-sm text-admin_text/60 mb-1"
          }
        >
          Select date{range ? "s" : ""}
        </div>
        <div className={variants?.input?.value || "text-lg text-admin_text"}>
          {formatDisplayDate()}
        </div>
      </div>

      {isOpen && (
        <div
          className={
            variants?.container?.modal ||
            "fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          }
        >
          <div
            className={
              variants?.container?.mainContainer ||
              "bg-admin_paper rounded-[28px] max-w-[360px] w-full shadow-xl relative"
            }
          >
            {showMonthSelector ? (
              <MonthSelector
                viewDate={viewDate}
                onSelect={handleDateSelection}
                onClose={() => setShowMonthSelector(false)}
                variants={variants}
              />
            ) : (
              <>
                <div className={variants?.container?.header || "px-6 pt-6"}>
                  <div className="flex items-center justify-between mb-4">
                    <h2
                      className={
                        variants?.header?.title ||
                        "text-admin_text text-base font-normal"
                      }
                    >
                      Select date{range ? "s" : ""}
                    </h2>
                    <XMarkIcon
                      className={
                        variants?.header?.closeButton ||
                        "w-6 h-6 text-admin_text/60 cursor-pointer"
                      }
                      onClick={() => setIsOpen(false)}
                    />
                  </div>

                  <div
                    className={
                      variants?.header?.selectedDate ||
                      "text-[32px] text-admin_text font-normal mb-4"
                    }
                  >
                    {formatDisplayDate()}
                  </div>

                  <button
                    onClick={() => setShowMonthSelector(true)}
                    className={
                      variants?.header?.monthSelector ||
                      "text-admin_text hover:bg-text/10 px-4 py-2 rounded-lg mb-4 flex items-center"
                    }
                  >
                    {viewDate.format(monthFormat)}
                    <ChevronDownIcon className="w-5 h-5 ml-2" />
                  </button>
                </div>

                <div className={variants?.container?.body || "px-6 pb-6"}>
                  <div className="grid grid-cols-7 mb-2">
                    {moment.weekdaysMin().map((day) => (
                      <div
                        key={day}
                        className={
                          variants?.calendar?.weekdayHeader ||
                          "text-center text-sm py-2 text-admin_text/60"
                        }
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  <div
                    className={
                      variants?.calendar?.daysGrid || "grid grid-cols-7"
                    }
                  >
                    {generateDays().map((date, idx) => {
                      const isSelected = range 
                        ? isRangeStart(date) || isRangeEnd(date)
                        : dates.start && date.isSame(dates.start, "day")
                      const isToday = date.isSame(moment(), "day")
                      const isCurrentMonth = date.isSame(viewDate, "month")
                      
                      return (
                        <button
                          key={`${date.format('YYYY-MM-DD')}-${forceRender}`}
                          onClick={() => handleDateSelect(date)}
                          onMouseEnter={() =>
                            selectionStep === "end" && setHoverDate(date)
                          }
                          onMouseLeave={() => setHoverDate(null)}
                          className={twMerge(
                            variants?.calendar?.dayButton?.base ||
                              "relative aspect-square flex items-center justify-center text-sm text-admin_text rounded-full hover:bg-primary",
                            // Override hover for selected dates in single mode to prevent text invisibility
                            isSelected && !range && "hover:bg-admin_primary/10",
                            !isCurrentMonth &&
                              (variants?.calendar?.dayButton?.otherMonth ||
                                "text-admin_text/40"),
                            getRangeStyle(date)
                          )}
                        >
                          <div
                            className={twMerge(
                              variants?.calendar?.dayButton?.dayWrapper ||
                                "w-10 h-10 flex items-center justify-center rounded-full",
                              isSelected &&
                                !range &&
                                "!border !border-admin_primary !text-admin_primary",
                              isToday &&
                                !isSelected &&
                                !isInRange(date) &&
                                (variants?.calendar?.dayButton?.today ||
                                  "font-bold text-admin_primary")
                            )}
                          >
                            {date.format("D")}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div
                  className={
                    variants?.container?.footer ||
                    "flex items-center justify-between p-4 border-t border-text/10"
                  }
                >
                  {showToday && (
                    <button
                      onClick={handleTodayClick}
                      className={twMerge(
                        variants?.buttons?.base || "px-6 py-2 rounded-full",
                        variants?.buttons?.secondary ||
                          "text-primary hover:bg-text/10"
                      )}
                    >
                      Today
                    </button>
                  )}

                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        setIsOpen(false)
                        setSelectionStep("start")
                      }}
                      className={twMerge(
                        variants?.buttons?.base || "px-6 py-2 rounded-full",
                        variants?.buttons?.secondary ||
                          "text-primary hover:bg-text/10"
                      )}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        console.log('[DatePicker] OK button clicked:', {
                          range,
                          'dates.start': dates.start,
                          'dates.start.isValid()': dates.start?.isValid(),
                          'dates.end': dates.end,
                          'dates.end.isValid()': dates.end?.isValid()
                        })

                        if (!range && dates.start && dates.start.isValid()) {
                          const dateValue = dates.start.toDate()
                          console.log('[DatePicker] Calling onChange with single date:', dateValue)
                          onChange?.(dateValue)
                          setIsOpen(false)
                          setSelectionStep("start")
                        } else if (range && dates.start && dates.start.isValid() && dates.end && dates.end.isValid()) {
                          const rangeValue = {
                            startDate: dates.start.toDate(),
                            endDate: dates.end.toDate(),
                          }
                          console.log('[DatePicker] Calling onChange with range:', rangeValue)
                          onChange?.(rangeValue)
                          setIsOpen(false)
                          setSelectionStep("start")
                        } else {
                          console.log('[DatePicker] OK button clicked but dates not valid - not calling onChange')
                        }
                      }}
                      disabled={
                        !range
                          ? !dates.start || !dates.start.isValid()
                          : !dates.start || !dates.start.isValid() || !dates.end || !dates.end.isValid()
                      }
                      className={twMerge(
                        variants?.buttons?.base || "px-6 py-2 rounded-full",
                        variants?.buttons?.primary ||
                          "text-primary hover:bg-primary/10",
                        (!range ? !dates.start || !dates.start.isValid() : !dates.start || !dates.start.isValid() || !dates.end || !dates.end.isValid())
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      )}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default DatePicker
